import { router } from "@inertiajs/react"
import { useEffect, useRef } from "react"

import type { NotificationItem } from "@/hooks/use-notification-feed"

/**
 * Notification kinds that mean the diner's server-rendered props went stale.
 *
 * `bill_closed` is deliberately absent: VisitClosedWatcher navigates to the
 * farewell on it, and a partial reload racing that navigation buys nothing.
 */
const RELOADABLE_KINDS = new Set([
  "order_created",
  "order_status_changed",
  "payment_succeeded",
  "payment_failed",
  // Staff answered the ring. `waiterCallPending` is a shell prop the diner's
  // call button renders straight, so without this the button keeps pulsing "a
  // waiter is on the way" after one already came — the reload is the only
  // thing that ever asks the server for that flag again.
  "waiter_call_acknowledged",
])

const DEFAULT_DEBOUNCE_MS = 250

/**
 * Which props each diner screen owns, mirroring `pathReloadTargets` in
 * use-location-realtime.ts. `diner` and `cartCount` ride along everywhere
 * because both are shared props rendered by the shell, not by the screen — the
 * top bar names the location and the tab bar carries the cart badge on every
 * page, so the fallback resyncs the shell rather than nothing.
 *
 * `router.reload({ only: [] })` is a full reload, not a no-op, so this never
 * returns an empty list.
 */
const SHELL_TARGETS = ["diner", "cartCount"]

export function dinerReloadTargets(url: string): string[] {
  const path = url.split("?")[0].replace(/\/+$/, "") || "/"

  if (path === "/dinein/orders") return ["orders", ...SHELL_TARGETS]
  // Tracking stayed outside /dinein: it is keyed to the order, and a pickup
  // diner watches it too.
  if (path.startsWith("/orders/")) return ["order", ...SHELL_TARGETS]
  if (path === "/dinein/cart" || path.startsWith("/bills")) return ["bill", ...SHELL_TARGETS]
  // Home is the restaurant directory now. Nothing a diner is notified about
  // changes which restaurants exist, so the shell targets are the whole job —
  // and `diner` is what flips home out of no-context mode once a visit starts.

  // Copied, not handed out: the caller owns the array it passes to `only`.
  return [...SHELL_TARGETS]
}

type UseDinerReloadOptions = {
  notifications: NotificationItem[]
  /** False until the feed's first payload has settled. */
  initialized: boolean
  /** Current Inertia URL. */
  path: string
  debounceMs?: number
}

/**
 * Turns the diner's notification feed into Inertia partial reloads.
 *
 * The diner is wired to the SSE feed but, until this hook, only ever rendered it
 * into the bell: staff moving an order to `ready` or adding a dish to the bill
 * left the visible data stale until a manual refresh. Staff solves the same
 * problem over ActionCable (`useLocationRealtime`); the diner reuses the stream
 * it already holds open, so there is one connection and one fallback to reason
 * about, and it inherits the feed's 7s polling degradation for free.
 */
export function useDinerReload({
  notifications,
  initialized,
  path,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UseDinerReloadOptions) {
  const pathRef = useRef(path)
  const watermarkRef = useRef<number | null>(null)
  const timerRef = useRef<number | null>(null)

  pathRef.current = path

  useEffect(() => {
    if (!initialized) {
      watermarkRef.current = null
      return
    }

    const highestId = notifications.reduce(
      (highest, notification) => Math.max(highest, notification.id),
      0
    )

    // The feed opens with history — it describes the page we just rendered, not
    // news about it. Take it as the watermark and stay put.
    if (watermarkRef.current === null) {
      watermarkRef.current = highestId
      return
    }

    const watermark = watermarkRef.current
    if (highestId <= watermark) return

    watermarkRef.current = highestId

    const wentStale = notifications.some(
      (notification) => notification.id > watermark && RELOADABLE_KINDS.has(notification.kind)
    )
    if (!wentStale) return
    if (timerRef.current) return

    // Debounced: a single staff action can emit several events, and a diner
    // watching a kitchen work through a table should cost one round trip.
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null

      // Read the path at flush time: the diner may have navigated mid-debounce.
      router.reload({ only: dinerReloadTargets(pathRef.current) })
    }, debounceMs)
  }, [initialized, notifications, debounceMs])

  useEffect(
    () => () => {
      if (!timerRef.current) return

      window.clearTimeout(timerRef.current)
      timerRef.current = null
    },
    []
  )
}
